import { LOCALE_DEFAULTS } from '../config/appConstants';

// ── Module-level locale config (set once via configureDateLocale) ──
let _locale = LOCALE_DEFAULTS.locale;
let _hour12 = !LOCALE_DEFAULTS.use24HourTime;

/**
 * Call once (e.g., from SettingsContext) to configure locale/time format
 * for all date helper functions.
 */
export const configureDateLocale = (settings) => {
    _locale = settings?.locale || LOCALE_DEFAULTS.locale;
    _hour12 = settings?.use24HourTime != null ? !settings.use24HourTime : !LOCALE_DEFAULTS.use24HourTime;
};

/**
 * Parse a date string from the server as UTC.
 * Server sends dates without 'Z' suffix, so we need to append it.
 * Returns a Date object representing the UTC time.
 */
export const parseUtcDate = (dateString) => {
    if (!dateString) return null;
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString);
};

/**
 * Get the day key for grouping (Today, Tomorrow, or formatted date).
 * Uses browser local time for comparison.
 */
export const getDayKeyET = (dateString) => {
    if (!dateString) return 'Unknown';

    const date = parseUtcDate(dateString);
    if (!date || isNaN(date.getTime())) return 'Unknown';

    const now = new Date();

    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    if (compareDate.getTime() === today.getTime()) {
        return 'Today';
    } else if (compareDate.getTime() === tomorrow.getTime()) {
        return 'Tomorrow';
    } else {
        return date.toLocaleDateString(_locale, { weekday: 'long', month: 'short', day: 'numeric' });
    }
};

/**
 * Get the week start (Sunday) for a given date.
 * Accepts either a Date object or a date string.
 * Used for grouping rides by week.
 */
export const getWeekStart = (dateInput) => {
    const date = dateInput instanceof Date ? dateInput : parseUtcDate(dateInput);
    if (!date || isNaN(date.getTime())) return null;

    const d = new Date(date);
    const dayOfWeek = d.getDay();
    d.setDate(d.getDate() - dayOfWeek);
    d.setHours(0, 0, 0, 0);
    return d;
};

/**
 * Get a formatted week label for grouping.
 * Returns a string like "Week of Feb 2 - Feb 8"
 */
export const getWeekLabel = (weekStart) => {
    if (!weekStart) return 'Unknown Week';

    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const startMonth = weekStart.toLocaleDateString(_locale, { month: 'short' });
    const startDay = weekStart.getDate();
    const endMonth = weekEnd.toLocaleDateString(_locale, { month: 'short' });
    const endDay = weekEnd.getDate();

    if (startMonth === endMonth) {
        return `Week of ${startMonth} ${startDay} - ${endDay}`;
    }
    return `Week of ${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

export const formatTime = (dateString) => {
    if (!dateString) return '--';
    try {
        const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        const date = new Date(utcString);
        return date.toLocaleTimeString(_locale, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: _hour12
        });
    } catch {
        return '--';
    }
};

/**
 * Format a date string to display date and time (e.g., "Dec 4, 2:30 PM")
 */
export const formatDateTime = (dateString) => {
    if (!dateString) return '--';
    try {
        const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        const date = new Date(utcString);
        return date.toLocaleString(_locale, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: _hour12
        });
    } catch {
        return '--';
    }
};

/**
 * Format a date string to display date only (e.g., "Dec 4, 2025")
 */
export const formatDate = (dateString) => {
    if (!dateString) return '--';
    try {
        const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
        const date = new Date(utcString);
        return date.toLocaleDateString(_locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    } catch {
        return '--';
    }
};

/**
 * Format a TimeOnly string (HH:mm:ss) to configured time format
 */
export const formatTimeOnly = (timeString) => {
    if (!timeString) return '--';
    try {
        // Parse time string (format: "HH:mm:ss" or "HH:mm")
        const [hours, minutes] = timeString.split(':').map(Number);
        const minuteStr = minutes.toString().padStart(2, '0');
        if (_hour12) {
            const period = hours >= 12 ? 'PM' : 'AM';
            const h12 = hours % 12 || 12;
            return `${h12}:${minuteStr} ${period}`;
        }
        return `${hours.toString().padStart(2, '0')}:${minuteStr}`;
    } catch {
        return '--';
    }
};

/**
 * Format a date string for day label (Today, Tomorrow, or weekday name)
 */
export const formatDayLabel = (dateString) => {
    if (!dateString) return '';

    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const scheduledDate = new Date(utcString);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);

    const isSameDay = (d1, d2) =>
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();

    if (isSameDay(scheduledDate, today)) {
        return 'Today';
    } else if (isSameDay(scheduledDate, tomorrow)) {
        return 'Tomorrow';
    } else {
        return scheduledDate.toLocaleDateString(_locale, { weekday: 'long' });
    }
};

/**
 * Check if two date strings are on different calendar days
 */
export const areDifferentDays = (dateString1, dateString2) => {
    if (!dateString1 || !dateString2) return true;
    const date1 = parseUtcDate(dateString1);
    const date2 = parseUtcDate(dateString2);
    if (!date1 || !date2) return true;
    return date1.getFullYear() !== date2.getFullYear() ||
        date1.getMonth() !== date2.getMonth() ||
        date1.getDate() !== date2.getDate();
};

/**
 * Format a date for the date divider in messaging
 * Returns "Today", "Yesterday", weekday name (within 7 days), or "Mon, Feb 15"
 */
export const formatDateDivider = (dateString) => {
    if (!dateString) return '';
    const date = parseUtcDate(dateString);
    if (!date || isNaN(date.getTime())) return '';

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.floor((today - messageDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays > 1 && diffDays < 7) {
        return date.toLocaleDateString(_locale, { weekday: 'long' });
    }
    // Older than 7 days - show date
    const options = { weekday: 'short', month: 'short', day: 'numeric' };
    if (date.getFullYear() !== now.getFullYear()) {
        options.year = 'numeric';
    }
    return date.toLocaleDateString(_locale, options);
};
